import React, { useContext } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Link } from 'react-router-dom';
import { AuthContext } from '../../contexts/AuthContext';

/**
 * Renders structured blog payload with clearer visual hierarchy (accent headings, section breaks, CTA block).
 */
export default function BlogArticleBody({ payload, colors }) {
  const { user } = useContext(AuthContext) || {};
  if (!payload?.content) return null;

  const { content, author, compliance } = payload;
  const text = colors?.text || 'var(--text-primary, #e2e8f0)';
  const muted = colors?.textSecondary || 'var(--text-secondary, #94a3b8)';
  const border = colors?.border || 'rgba(148,163,184,0.2)';
  const link = colors?.primary || '#3b82f6';
  const accent = colors?.primary || '#38bdf8';
  const cardBg = colors?.bgCard || 'rgba(148,163,184,0.06)';
  const startTodayTo = user
    ? '/trade'
    : (content.cta?.target_url?.startsWith('/') ? content.cta.target_url : '/register');

  const mdComponents = {
    a: ({ href, children }) => {
      if (href && href.startsWith('/')) {
        return (
          <Link to={href} style={{ color: link }}>
            {children}
          </Link>
        );
      }
      return (
        <a href={href} target="_blank" rel="noopener noreferrer" style={{ color: link }}>
          {children}
        </a>
      );
    },
    p: ({ children }) => (
      <p className="blog-md-p" style={{ color: text }}>
        {children}
      </p>
    ),
    strong: ({ children }) => (
      <strong style={{ color: text, fontWeight: 650 }}>{children}</strong>
    ),
    li: ({ children }) => (
      <li className="blog-md-li" style={{ color: text }}>{children}</li>
    ),
    ul: ({ children }) => <ul className="blog-md-list">{children}</ul>,
    ol: ({ children }) => <ol className="blog-md-list">{children}</ol>,
    blockquote: ({ children }) => (
      <blockquote className="blog-md-quote" style={{ borderColor: accent, color: muted }}>
        {children}
      </blockquote>
    ),
    table: ({ children }) => (
      <div className="blog-md-table-wrap">
        <table className="blog-md-table">{children}</table>
      </div>
    ),
    th: ({ children }) => (
      <th style={{ color: accent, borderColor: border }}>{children}</th>
    ),
    td: ({ children }) => (
      <td style={{ color: text, borderColor: border }}>{children}</td>
    ),
  };

  return (
    <article className="blog-article-body">
      {(content.sections || []).map((section, i) => (
        <section key={`${section.heading}-${i}`} className="blog-article-section">
          {section.heading && (
            <h2 className="blog-article-h2" style={{ color: text }}>
              <span className="blog-article-h2-mark" style={{ background: accent }} aria-hidden />
              {section.heading}
            </h2>
          )}
          {section.body && (
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
              {section.body}
            </ReactMarkdown>
          )}
          {(section.subsections || []).map((sub, j) => (
            <div key={`${sub.heading}-${j}`} className="blog-article-sub">
              {sub.heading && (
                <h3 className="blog-article-h3" style={{ color: accent }}>
                  {sub.heading}
                </h3>
              )}
              {sub.body && (
                <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
                  {sub.body}
                </ReactMarkdown>
              )}
            </div>
          ))}
        </section>
      ))}

      {(content.faq || []).length > 0 && (
        <section className="blog-article-faq">
          <h2 className="blog-article-h2" style={{ color: text }}>
            <span className="blog-article-h2-mark" style={{ background: accent }} aria-hidden />
            FAQ
          </h2>
          {content.faq.map((item, i) => (
            <div
              key={i}
              className="blog-faq-item"
              style={{ background: cardBg, borderColor: border }}
            >
              <h3 style={{ color: accent }}>{item.question}</h3>
              <p style={{ color: text }}>{item.answer_40_60_words}</p>
            </div>
          ))}
        </section>
      )}

      {content.cta?.text && (
        <div
          className="blog-article-cta"
          style={{ background: cardBg, borderColor: accent }}
        >
          <p className="blog-article-cta-label" style={{ color: muted }}>
            Next step
          </p>
          <p className="blog-article-cta-text" style={{ color: text }}>
            {content.cta.text}
          </p>
          <Link to={startTodayTo} className="blog-article-cta-btn">
            Start Today
          </Link>
        </div>
      )}

      {author && (
        <aside className="blog-article-author" style={{ borderColor: border }}>
          <p style={{ color: text }}>
            <span style={{ color: accent }}>{author.name}</span>
            {author.title ? ` · ${author.title}` : ''}
          </p>
          {author.bio && (
            <p style={{ color: muted }}>{author.bio}</p>
          )}
        </aside>
      )}

      {(compliance?.disclaimers || []).length > 0 && (
        <footer className="blog-article-disclaimers">
          {compliance.disclaimers.map((d, i) => (
            <p key={i} style={{ color: muted }}>{d}</p>
          ))}
        </footer>
      )}
    </article>
  );
}
